import type { ChatbotAttributionContext } from '@workspace/types';

const STORAGE_KEY = 'microsyst.leadAttribution.v1';

function value(params: URLSearchParams, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const found = params.get(key)?.trim();
    if (found) return found;
  }
  return undefined;
}

function hasAttribution(value: ChatbotAttributionContext): boolean {
  return Boolean(
    value.funnelRoute
    || value.campaignId
    || value.adId
    || value.formId
    || value.utmSource
    || value.gclid
    || value.fbclid,
  );
}

export function captureLeadAttribution(scope = 'default'): ChatbotAttributionContext | undefined {
  if (typeof window === 'undefined') return undefined;
  const storageKey = `${STORAGE_KEY}.${scope.slice(0, 128)}`;
  const params = new URLSearchParams(window.location.search);
  const gclid = value(params, 'gclid');
  const fbclid = value(params, 'fbclid');
  const utmSource = value(params, 'utm_source');
  const current: ChatbotAttributionContext = {
    source: (value(params, 'source') ?? utmSource ?? (gclid ? 'GOOGLE' : fbclid ? 'META' : undefined))?.toUpperCase(),
    medium: value(params, 'medium') ?? value(params, 'utm_medium'),
    funnelRoute: value(params, 'funnel_route'),
    campaignId: value(params, 'campaign_id', 'campaignid'),
    campaignName: value(params, 'campaign_name'),
    groupId: value(params, 'adset_id', 'adgroup_id', 'group_id'),
    groupName: value(params, 'adset_name', 'adgroup_name', 'group_name'),
    adId: value(params, 'ad_id', 'creative'),
    adName: value(params, 'ad_name'),
    formId: value(params, 'form_id'),
    utmSource,
    utmMedium: value(params, 'utm_medium'),
    utmCampaign: value(params, 'utm_campaign'),
    utmContent: value(params, 'utm_content'),
    utmTerm: value(params, 'utm_term'),
    gclid,
    fbclid,
    landingUrl: window.location.href.slice(0, 2000),
    referrer: document.referrer?.slice(0, 2000) || undefined,
  };

  let stored: ChatbotAttributionContext | undefined;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw) stored = JSON.parse(raw) as ChatbotAttributionContext;
  } catch {
    // Attribution must never block the chat.
  }

  if (hasAttribution(current)) {
    const firstTouch = stored ?? current;
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(firstTouch));
    } catch {
      // Non-blocking.
    }
    // The backend keeps FIRST immutable and updates LAST. Send the current touch
    // when a new attributed landing exists; otherwise stored FIRST is the fallback.
    return current;
  }
  return stored;
}
